import React from 'react';
import { Search, Target, Shield, Eye, Network, Database, AlertCircle, Activity } from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';
import { SEO } from '../../components/SEO';

export function ActiveHunting() {
  const { language } = useLanguage();

  const content = {
    hero: {
      title: {
        fr: 'Chasse Active aux Menaces',
        en: 'Active Threat Hunting'
      },
      subtitle: {
        fr: 'Détection proactive et élimination des menaces avancées',
        en: 'Proactive detection and elimination of advanced threats'
      }
    },
    features: [
      {
        icon: <Eye className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Surveillance Continue',
          en: 'Continuous Monitoring'
        },
        description: {
          fr: 'Analyse en temps réel des comportements suspects',
          en: 'Real-time analysis of suspicious behaviors'
        }
      },
      {
        icon: <Network className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Analyse Réseau',
          en: 'Network Analysis'
        },
        description: {
          fr: 'Détection des anomalies dans le trafic réseau',
          en: 'Detection of network traffic anomalies'
        }
      },
      {
        icon: <Database className="w-8 h-8 text-blue-500" />,
        title: {
          fr: 'Analyse des Données',
          en: 'Data Analysis'
        },
        description: {
          fr: 'Investigation approfondie des logs et événements',
          en: 'In-depth investigation of logs and events'
        }
      }
    ],
    methodology: {
      title: {
        fr: 'Notre Méthodologie',
        en: 'Our Methodology'
      },
      steps: [
        {
          icon: <Search className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Collecte de Données',
            en: 'Data Collection'
          },
          description: {
            fr: 'Agrégation des données de sécurité de toutes les sources',
            en: 'Aggregation of security data from all sources'
          }
        },
        {
          icon: <Activity className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Analyse Comportementale',
            en: 'Behavioral Analysis'
          },
          description: {
            fr: 'Détection des patterns et anomalies',
            en: 'Detection of patterns and anomalies'
          }
        },
        {
          icon: <AlertCircle className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Investigation',
            en: 'Investigation'
          },
          description: {
            fr: 'Analyse approfondie des menaces potentielles',
            en: 'In-depth analysis of potential threats'
          }
        },
        {
          icon: <Shield className="w-6 h-6 text-blue-500" />,
          title: {
            fr: 'Réponse',
            en: 'Response'
          },
          description: {
            fr: 'Mise en place de mesures de protection',
            en: 'Implementation of protective measures'
          }
        }
      ]
    },
    capabilities: {
      title: {
        fr: 'Nos Capacités',
        en: 'Our Capabilities'
      },
      items: [
        {
          title: {
            fr: 'Détection Avancée',
            en: 'Advanced Detection'
          },
          description: {
            fr: 'Utilisation de l\'IA et du machine learning',
            en: 'Using AI and machine learning'
          }
        },
        {
          title: {
            fr: 'Analyse Forensique',
            en: 'Forensic Analysis'
          },
          description: {
            fr: 'Investigation approfondie des incidents',
            en: 'In-depth incident investigation'
          }
        },
        {
          title: {
            fr: 'Threat Intelligence',
            en: 'Threat Intelligence'
          },
          description: {
            fr: 'Intégration des données de menaces globales',
            en: 'Integration of global threat data'
          }
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <SEO 
        title={content.hero.title[language]}
        description={content.hero.subtitle[language]}
        keywords={['threat hunting', 'active detection', 'security monitoring', 'threat analysis']}
      />

      {/* Hero Section */}
      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Target className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {content.hero.title[language]}
            </h1>
            <p className="text-xl text-gray-300">
              {content.hero.subtitle[language]}
            </p>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {content.features.map((feature, index) => (
            <div key={index} className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-lg border border-gray-700/50 hover:border-blue-500/50 transition-colors duration-300">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold text-white mb-3">
                {feature.title[language]}
              </h3>
              <p className="text-gray-300">
                {feature.description[language]}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Methodology Section */}
      <div className="bg-gray-800/50 py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            {content.methodology.title[language]}
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute left-8 inset-y-0 w-0.5 bg-blue-500/30"></div>
              <div className="space-y-12">
                {content.methodology.steps.map((step, index) => (
                  <div key={index} className="relative flex items-center gap-8">
                    <div className="absolute left-8 w-3 h-3 bg-blue-500 rounded-full transform -translate-x-1.5"></div>
                    <div className="pl-20">
                      <h3 className="text-xl font-bold text-white mb-2">
                        {step.title[language]}
                      </h3>
                      <p className="text-gray-300">
                        {step.description[language]}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Capabilities Section */}
      <div className="container mx-auto px-6 py-20">
        <h2 className="text-3xl font-bold text-white text-center mb-12">
          {content.capabilities.title[language]}
        </h2>
        <div className="max-w-4xl mx-auto space-y-8">
          {content.capabilities.items.map((item, index) => (
            <div key={index} className="flex items-start space-x-4 bg-gray-800/50 p-6 rounded-lg">
              <div className="flex-shrink-0 bg-blue-500/10 p-3 rounded-lg">
                <Shield className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {item.title[language]}
                </h3>
                <p className="text-gray-300">
                  {item.description[language]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80')] opacity-10 bg-cover bg-center"></div>
          <div className="relative z-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              {language === 'fr' ? 'Renforcez Votre Sécurité' : 'Strengthen Your Security'}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {language === 'fr' 
                ? 'Découvrez comment notre approche proactive peut protéger votre entreprise' 
                : 'Discover how our proactive approach can protect your business'}
            </p>
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              {language === 'fr' ? 'Commencer Maintenant' : 'Get Started Now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}